import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { TopicStat } from "../../constants/dashboardStats";
import SplitToggle, {
  MODULE_SORT_OPTIONS,
  ModuleSortOrder,
} from "../SplitToggle";
import TopicBreakdownRow from "../TopicBreakdownRow/TopicBreakdownRow";
import "./TopicBreakdownSection.scss";

const PAGE_SIZE = 10;

type TopicBreakdownSectionProps = {
  topics: TopicStat[];
  subjectId?: number;
  onPracticeTopic: (topic: TopicStat) => void;
  isLoading?: boolean;
};

const TopicBreakdownSection = ({
  topics,
  subjectId,
  onPracticeTopic,
  isLoading = false,
}: TopicBreakdownSectionProps) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [sortOrder, setSortOrder] = useState<ModuleSortOrder>("worst");

  const attemptedTopics = useMemo(
    () => topics.filter((topic) => topic.questionsDone > 0),
    [topics]
  );

  const sortedTopics = useMemo(
    () =>
      [...attemptedTopics].sort((a, b) => {
        const scoreCompare =
          sortOrder === "worst" ? a.score - b.score : b.score - a.score;
        return scoreCompare || a.name.localeCompare(b.name);
      }),
    [attemptedTopics, sortOrder]
  );

  const totalPages = Math.max(1, Math.ceil(sortedTopics.length / PAGE_SIZE));

  useEffect(() => {
    setCurrentPage(1);
  }, [topics, sortOrder]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedTopics = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return sortedTopics.slice(start, start + PAGE_SIZE);
  }, [sortedTopics, currentPage]);

  const pageStartIndex = (currentPage - 1) * PAGE_SIZE;

  return (
    <>
      <div className="topic-breakdown-section__header-row">
        <div className="topic-breakdown-section__header">Module breakdown</div>
        <SplitToggle
          value={sortOrder}
          onChange={setSortOrder}
          options={MODULE_SORT_OPTIONS}
          ariaLabel="Module sort order"
        />
      </div>
      <p className="topic-breakdown-section__note">
        Unattempted modules are hidden.{" "}
        {subjectId ? (
          <>
            <Link to={`/subjects/${subjectId}`}>View all modules on the subject page</Link>.
          </>
        ) : (
          <>View all modules on the subject page.</>
        )}
      </p>
      <section className="topic-breakdown-section">
        <div className="topic-breakdown-section__card">
          <div className="topic-breakdown-section__card-header">
            <span>Module</span>
            <span>Score · Questions done</span>
          </div>
          {isLoading ? (
            <p className="topic-breakdown-section__message">Loading modules…</p>
          ) : sortedTopics.length === 0 ? (
            <p className="topic-breakdown-section__message">
              No attempted modules yet.
              {subjectId ? (
                <>
                  {" "}
                  <Link to={`/subjects/${subjectId}`}>View all modules on the subject page</Link>.
                </>
              ) : null}
            </p>
          ) : (
            <>
              {paginatedTopics.map((topic, index) => (
                <TopicBreakdownRow
                  key={topic.moduleId ?? topic.name}
                  rank={pageStartIndex + index + 1}
                  topic={topic}
                  onPractice={onPracticeTopic}
                />
              ))}
              {totalPages > 1 && (
                <div className="topic-breakdown-section__pagination">
                  <button
                    type="button"
                    className="topic-breakdown-section__pagination-btn"
                    onClick={() => setCurrentPage((page) => page - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  <span className="topic-breakdown-section__pagination-label">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    type="button"
                    className="topic-breakdown-section__pagination-btn"
                    onClick={() => setCurrentPage((page) => page + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </>
  );
};

export default TopicBreakdownSection;
